/**
 * Stored searches as `/v1` requests (design
 * docs/shop-agent/shop-agent-platform-v1-search-migration.md §6, "What changes
 * for stored searches").
 *
 * Saved searches and search history hold a `StoredSearch`: the body
 * `POST /shop-agent/search/products` replays, so a replay runs exactly the
 * search that was stored.
 */

import type { ShopperSearchRequest, StoredSearch } from "../../openapi/shop_agent/client/types.gen";

/** Whether an untyped payload (a search history row's `query_params`) is a stored `/v1` request. */
export function isStoredSearch(
  value: StoredSearch | Record<string, unknown> | null | undefined
): value is StoredSearch {
  if (!value || typeof value !== "object") return false;
  const candidate = value as { format?: unknown; request?: unknown };
  return (
    candidate.format === "v1_request" &&
    typeof candidate.request === "object" &&
    candidate.request !== null
  );
}

/** `request` as stored: no `cursor`, since a replay starts at page 1. */
export function storedSearch(
  request: ShopperSearchRequest,
  browseMenuUuid?: string | null
): StoredSearch {
  const { cursor: _cursor, ...rest } = request;
  return {
    format: "v1_request",
    version: 1,
    request: rest,
    ...(browseMenuUuid ? { browse_menu_uuid: browseMenuUuid } : {}),
  };
}

/** The request a stored search replays, with the replaying surface's `limit` when given. */
export function replayRequest(stored: StoredSearch, limit?: number): ShopperSearchRequest {
  const { cursor: _cursor, ...request } = stored.request;
  return limit == null ? request : { ...request, limit };
}

type FacetChip = { name: string; values: string[] };

/**
 * The filter chips a saved search shows: the facets its replay ran (its own
 * until the replay answers).
 */
export function savedSearchChips(
  query: StoredSearch | null | undefined,
  ranFacets: FacetChip[] | null
): FacetChip[] {
  if (!query) return [];
  return ranFacets ?? query.request.facets ?? [];
}
